<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-4xl mx-auto w-full px-4 py-8">
      <h1 class="text-2xl font-bold text-[#152a34] mb-6">Minha Agenda</h1>

      <!-- Banner: agenda não configurada -->
      <div
        v-if="showAvailabilityWarning"
        class="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-5 flex items-start gap-3 text-amber-800 text-sm"
      >
        <span class="mdi mdi-calendar-alert text-xl flex-shrink-0 mt-0.5"></span>
        <div>
          <p class="font-semibold">Você ainda não configurou sua disponibilidade semanal.</p>
          <p class="mt-1">Sem isso, clientes não conseguem agendar horários com você. Configure na aba <strong>Disponibilidade</strong> abaixo.</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex gap-2 mb-6">
        <button v-for="tab in tabs" :key="tab.key" @click="activeTab = tab.key"
          :class="['px-5 py-2 rounded-xl text-sm font-medium transition', activeTab === tab.key ? 'bg-[#152a34] text-white' : 'bg-white border border-gray-200 hover:border-[#b99a52]']">
          {{ tab.label }}
        </button>
      </div>

      <!-- Aba 1: Disponibilidade semanal -->
      <div v-if="activeTab === 'weekly'">
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-6 mb-4">
          <h2 class="font-semibold text-[#152a34] mb-4">Horário de Trabalho Semanal</h2>
          <div class="space-y-3">
            <div v-for="day in weekDays" :key="day.value" class="flex items-center gap-4 py-2 border-b border-gray-50">
              <div class="w-24 flex items-center gap-2">
                <input type="checkbox" v-model="day.active" class="accent-[#b99a52]" />
                <span class="text-sm font-medium">{{ day.label }}</span>
              </div>
              <template v-if="day.active">
                <input v-model="day.start_time" type="time" class="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
                <span class="text-gray-400 text-sm">até</span>
                <input v-model="day.end_time" type="time" class="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
              </template>
              <span v-else class="text-sm text-gray-400">Folga</span>
            </div>
          </div>

          <div v-if="availabilityDirty && !savingAvailability" class="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-xl text-amber-800 text-sm flex items-center gap-2">
            <span class="mdi mdi-alert-circle text-lg flex-shrink-0"></span>
            Você tem alterações não salvas. Clique em <strong class="mx-1">Salvar Disponibilidade</strong> para confirmar.
          </div>

          <div v-if="availabilityMsg" :class="['mt-4 p-3 rounded-xl text-sm', availabilityError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700']">
            {{ availabilityMsg }}
          </div>

          <button @click="saveAvailability" :disabled="savingAvailability"
            :class="[
              'mt-5 w-full py-3 rounded-xl font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2',
              availabilityDirty && !savingAvailability
                ? 'bg-[#b99a52] text-white hover:bg-[#a08840] ring-2 ring-[#b99a52] ring-offset-2'
                : 'bg-[#b99a52] text-white hover:bg-[#a08840]'
            ]">
            <span v-if="savingAvailability" class="mdi mdi-loading animate-spin"></span>
            <span v-else-if="availabilityDirty" class="mdi mdi-content-save"></span>
            {{ savingAvailability ? 'Salvando...' : 'Salvar Disponibilidade' }}
          </button>
        </div>
      </div>

      <!-- Aba 2: Agendamentos -->
      <div v-if="activeTab === 'appointments'">
        <!-- Filtro de status -->
        <div class="flex gap-2 flex-wrap mb-4">
          <button v-for="f in statusFilters" :key="f.value" @click="apptStatus = f.value"
            :class="['px-3 py-1 rounded-full text-xs font-medium transition', apptStatus === f.value ? 'bg-[#152a34] text-white' : 'bg-white border border-gray-200 hover:border-[#b99a52]']">
            {{ f.label }}
          </button>
        </div>

        <div v-if="loadingAppts" class="space-y-3">
          <div v-for="i in 4" :key="i" class="bg-white rounded-2xl shadow p-5 animate-pulse h-20"></div>
        </div>

        <div v-else-if="appointments.length === 0" class="text-center py-16 text-gray-400">
          <span class="mdi mdi-calendar-blank text-4xl block mb-2"></span>
          <p>Nenhum agendamento.</p>
        </div>

        <div v-else class="space-y-3">
          <router-link v-for="appt in appointments" :key="appt.id" :to="{ name: 'appointment-details', params: { id: appt.id } }"
            class="block bg-white rounded-2xl border border-[#e0dcd6] shadow p-4 hover:border-[#b99a52] transition">
            <div class="flex items-start justify-between">
              <div>
                <p class="font-semibold text-[#152a34] text-sm">{{ appt.service?.name }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ appt.contractor?.name }}</p>
                <p class="text-xs text-gray-400 mt-1">{{ formatDate(appt.scheduled_at) }}</p>
              </div>
              <span :class="['px-2 py-0.5 rounded-full text-xs font-medium', statusClass(appt.status)]">{{ statusLabel(appt.status) }}</span>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Aba 3: Bloqueios -->
      <div v-if="activeTab === 'blocks'">
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-6 mb-4">
          <h2 class="font-semibold text-[#152a34] mb-4">Adicionar Bloqueio</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-medium block mb-1">Data *</label>
              <input v-model="newBlock.blocked_date" type="date" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Motivo</label>
              <input v-model="newBlock.reason" type="text" placeholder="Férias, feriado..." class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Horário início (deixe vazio = dia inteiro)</label>
              <input v-model="newBlock.start_time" type="time" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Horário fim</label>
              <input v-model="newBlock.end_time" type="time" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
          </div>
          <button @click="addBlock" :disabled="savingBlock" class="mt-4 px-6 py-2 bg-[#152a34] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a4a] transition disabled:opacity-50">
            <span v-if="savingBlock" class="mdi mdi-loading animate-spin mr-1"></span>
            Adicionar Bloqueio
          </button>
        </div>

        <!-- Lista de bloqueios -->
        <div v-if="blocks.length === 0" class="text-center py-12 text-gray-400 text-sm">Nenhum bloqueio cadastrado.</div>
        <div v-else class="space-y-2">
          <div v-for="block in blocks" :key="block.id" class="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <p class="font-medium text-sm text-[#152a34]">{{ formatBlockDate(block.blocked_date) }}</p>
              <p class="text-xs text-gray-400">
                {{ block.start_time ? `${block.start_time} - ${block.end_time}` : 'Dia inteiro' }}
                {{ block.reason ? ` — ${block.reason}` : '' }}
              </p>
            </div>
            <button @click="removeBlock(block.id)" class="p-2 text-red-400 hover:text-red-600 transition">
              <span class="mdi mdi-delete"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue'
import { getMyAvailability, saveMyAvailability, listMyBlocks, createBlock, deleteBlock, getProviderAppointments } from '@/services/api'
import { useProviderOnboardingStore } from '@/store/providerOnboardingStore'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const onboardingStore = useProviderOnboardingStore()
const activeTab = ref('weekly')
const showAvailabilityWarning = ref(false)
const tabs = [
  { key: 'weekly', label: 'Disponibilidade' },
  { key: 'appointments', label: 'Agendamentos' },
  { key: 'blocks', label: 'Bloqueios' }
]

// Disponibilidade semanal
const weekDays = ref([
  { value: 0, label: 'Dom', active: false, start_time: '08:00', end_time: '18:00' },
  { value: 1, label: 'Seg', active: true, start_time: '08:00', end_time: '18:00' },
  { value: 2, label: 'Ter', active: true, start_time: '08:00', end_time: '18:00' },
  { value: 3, label: 'Qua', active: true, start_time: '08:00', end_time: '18:00' },
  { value: 4, label: 'Qui', active: true, start_time: '08:00', end_time: '18:00' },
  { value: 5, label: 'Sex', active: true, start_time: '08:00', end_time: '18:00' },
  { value: 6, label: 'Sáb', active: false, start_time: '08:00', end_time: '13:00' }
])
const savingAvailability = ref(false)
const availabilityMsg = ref('')
const availabilityError = ref(false)
const availabilityDirty = ref(false)
const availabilityLoaded = ref(false)

// Agendamentos
const appointments = ref([])
const loadingAppts = ref(false)
const apptStatus = ref('')
const statusFilters = [
  { label: 'Todos', value: '' },
  { label: 'Aguardando', value: 'pending' },
  { label: 'Confirmados', value: 'confirmed' },
  { label: 'Concluídos', value: 'completed' }
]

// Bloqueios
const blocks = ref([])
const newBlock = ref({ blocked_date: '', start_time: '', end_time: '', reason: '' })
const savingBlock = ref(false)

function statusLabel (s) {
  const m = { pending: 'Aguardando', confirmed: 'Confirmado', in_progress: 'Em andamento', awaiting_payment: 'Pagar', completed: 'Concluído', cancelled: 'Cancelado' }
  return m[s] || s
}

function statusClass (s) {
  const m = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', in_progress: 'bg-indigo-100 text-indigo-700', awaiting_payment: 'bg-orange-100 text-orange-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' }
  return m[s] || 'bg-gray-100 text-gray-600'
}

function formatDate (d) {
  if (!d) return ''
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function formatBlockDate (d) {
  if (!d) return ''
  return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
}

async function saveAvailability () {
  savingAvailability.value = true
  availabilityMsg.value = ''
  availabilityError.value = false
  try {
    const data = weekDays.value.map(d => ({
      day_of_week: d.value,
      start_time: d.start_time,
      end_time: d.end_time,
      is_active: d.active
    }))
    await saveMyAvailability({ availability: data })
    availabilityMsg.value = 'Disponibilidade salva com sucesso!'
    availabilityDirty.value = false
    showAvailabilityWarning.value = false
    onboardingStore.refresh()
  } catch (err) {
    availabilityError.value = true
    availabilityMsg.value = err?.response?.data?.error || 'Erro ao salvar.'
  } finally {
    savingAvailability.value = false
  }
}

async function loadAvailability () {
  availabilityLoaded.value = false
  try {
    const { data } = await getMyAvailability()
    const records = data.data || []
    records.forEach(a => {
      const day = weekDays.value.find(d => d.value === a.day_of_week)
      if (day) {
        day.active = !!a.is_active
        day.start_time = a.start_time?.slice(0, 5) || '08:00'
        day.end_time = a.end_time?.slice(0, 5) || '18:00'
      }
    })
    await nextTick()
    const neverSaved = records.length === 0
    availabilityDirty.value = neverSaved
    showAvailabilityWarning.value = neverSaved
  } catch {
    await nextTick()
    availabilityDirty.value = false
    showAvailabilityWarning.value = false
  }
  availabilityLoaded.value = true
}

watch(
  () => weekDays.value.map(d => `${d.value}:${d.active}:${d.start_time}:${d.end_time}`).join('|'),
  () => {
    if (availabilityLoaded.value) availabilityDirty.value = true
  }
)

async function loadAppointments () {
  loadingAppts.value = true
  try {
    const params = { limit: 50 }
    if (apptStatus.value) params.status = apptStatus.value
    const { data } = await getProviderAppointments(params)
    appointments.value = data.data || []
  } catch { appointments.value = [] }
  finally { loadingAppts.value = false }
}

async function loadBlocks () {
  try {
    const { data } = await listMyBlocks()
    blocks.value = data.data || []
  } catch { blocks.value = [] }
}

async function addBlock () {
  if (!newBlock.value.blocked_date) return
  savingBlock.value = true
  try {
    await createBlock(newBlock.value)
    newBlock.value = { blocked_date: '', start_time: '', end_time: '', reason: '' }
    await loadBlocks()
  } catch {} finally { savingBlock.value = false }
}

async function removeBlock (id) {
  try {
    await deleteBlock(id)
    blocks.value = blocks.value.filter(b => b.id !== id)
  } catch {}
}

watch(apptStatus, loadAppointments)

onMounted(() => {
  loadAvailability()
  loadAppointments()
  loadBlocks()
})
</script>
